import { useContext, useEffect, useReducer } from 'react'
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material'
import {
    Add as AddIcon,
    LocalOffer as LocalOfferIcon,
    Style as StyleIcon,
} from '@mui/icons-material'
import { Autocomplete } from '.'
import { useAuth } from '../../context/AuthenticationContext'
import { ToastMessage } from '../../context/ToastPopUpContext'
import { getManufacturers, getTags } from '../../apiRoutes'

interface tags {
    addTag?: any
    row?: any
    pageType?: string
    setFormDirty?: any
    entryId?: any
}

const UniversalTagToolbar = ({
    addTag,
    row,
    pageType,
    setFormDirty,
    entryId,
}: tags) => {
    const tagsForMapping = row?.tags
    const filteredTags = tagsForMapping?.map((tag) => tag.tag)

    const currentUser = useAuth()

    const { activeManufacturer } = currentUser.auth

    const defaultState = {
        tags: filteredTags || [],
        tag: '',
        availableTags: [],
        tagData: [],
        addingTags: false,
        manufacturers: [],
        taggedItem: row?._id || '',
        manufacturerName: row?.manufacturerName || activeManufacturer,
    }

    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const setToastMessage = useContext(ToastMessage)

    const {
        tags,
        availableTags,
        addingTags,
        tagData,
        tag,
        manufacturers,
        taggedItem,
        manufacturerName,
    } = state

    const getData = async (active) => {
        let manufacturers: any = []
        let availableTags: any = []
        let tagData: any = []

        try {
            const data = await getTags(active)
            const getManufacturerData = await getManufacturers(true)

            if (data && data.length !== 0) {
                const filterTags = data.map((tag) => tag.tag)

                availableTags = filterTags
                tagData = data
            }
            if (getManufacturerData && getManufacturerData.length !== 0) {
                manufacturers = getManufacturerData.filter(
                    (manufacturer) => manufacturer
                )
            } else {
                setState({ availableTags: [] })
            }
            setState({ availableTags, tagData, manufacturers })
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    useEffect(() => {
        const handleSubmit = (tags, tagData) => {
            let taggedManufacturer = activeManufacturer
            let page: any = pageType
            let taggedItems: any = {}
            let taggedId = taggedItem === '' ? entryId : taggedItem

            const pagedata = {
                pageType: pageType,
                taggedId: taggedItem === '' ? entryId : row?._id,
            }

            taggedItems = {
                ...taggedItems,
                [page]: [
                    ...(taggedItems[page] || []).filter(
                        (item: any) => item?._id !== taggedId
                    ),
                    taggedId,
                ],
            }

            const findManufacturer = manufacturers?.find(
                ({ manufacturerName }) =>
                    manufacturerName === taggedManufacturer
            )

            const existingTags = tagData.map((data) => data.tag)

            const removeTags = (row?.tags || []).filter(
                (tagObj) => !tags.includes(tagObj.tag)
            )
            const removedTags = [...removeTags, pagedata]

            const matchedTags = tagData
                .filter((data) => tags.includes(data.tag))
                .map((data) => {
                    const updatedTaggedItems: any = {
                        ...data.taggedItems,
                        [page]: [
                            ...(data.taggedItems?.[page] || []).filter(
                                (item) => item?._id !== taggedId
                            ),
                            taggedId,
                        ],
                    }

                    return {
                        ...data,
                        taggedItems: updatedTaggedItems,
                        pageType,
                    }
                })

            const newTags = tags
                .filter((tag) => !existingTags.includes(tag))
                .map((tag) => ({
                    tag,
                    manufacturer: findManufacturer,
                    taggedItems: taggedItems,
                    pageType,
                }))

            const compiledTags = [...matchedTags, ...newTags]

            if (addTag) {
                addTag(compiledTags, removedTags)
            } else {
                console.warn('addTag function is not provided.')
            }
        }

        if (tags && tagData) {
            handleSubmit(tags, tagData)
        }
    }, [tagData, tags, entryId])

    const handleStartTagging = () => {
        setState({ addingTags: true })
    }

    const handleTagChange = (e: any) => {
        const { name, value } = e.target

        if (name === 'tag') {
            setState({ tag: value })
        }
    }

    const handleTagSelectionChange = (event) => {
        const tagValue = event.target.value
        setState({ tags: [...tags, tagValue], tag: tagValue })
        addTag ? setFormDirty() : null
    }

    const saveCustomTag = () => {
        const newTag = tag
        if (!newTag) return

        const existingTagIndex = tags.indexOf(newTag)
        if (existingTagIndex === -1) {
            setState({ tags: [...tags, newTag], tag: '' })
            addTag ? setFormDirty() : null
        } else {
            const updatedTags = [...tags]
            updatedTags[existingTagIndex] = newTag
            setState({ tags: updatedTags, tag: '' })
            addTag ? setFormDirty() : null
        }
    }

    const removeTag = (tag) => {
        const updatedTags = tags.filter((tags) => tags !== tag)

        setState({ tags: updatedTags })

        addTag ? setFormDirty() : null
    }

    const filterOptions = (options, savedTags) => {
        return options.filter((option) => !savedTags.includes(option))
    }

    useEffect(() => {
        getData(true)
    }, [])

    const manufacturerCheck = activeManufacturer === manufacturerName

    if (!manufacturerCheck) return null

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
                rowGap: 1,
                py: 1.5,
                px: 0.5,
                borderTop: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: 'text.secondary',
                    flexShrink: 0,
                }}
            >
                <StyleIcon sx={{ fontSize: 18 }} />
                <Typography
                    variant="caption"
                    sx={{
                        textTransform: 'uppercase',
                        letterSpacing: '0.07em',
                        fontSize: 10.5,
                        fontWeight: 600,
                    }}
                >
                    Tags
                </Typography>
            </Box>

            {tags && tags.length ? (
                tags.map((tagLabel, index) => (
                    <Chip
                        key={index}
                        size="small"
                        variant="outlined"
                        label={tagLabel}
                        onDelete={() => removeTag(tagLabel)}
                        icon={<LocalOfferIcon sx={{ fontSize: 13 }} />}
                        sx={{ fontWeight: 600 }}
                    />
                ))
            ) : (
                <Typography
                    variant="body2"
                    sx={{ color: 'text.disabled', fontStyle: 'italic' }}
                >
                    No tags yet
                </Typography>
            )}

            {addingTags ? (
                <Box sx={{ minWidth: 220, ml: 0.5 }}>
                    <Autocomplete
                        name="tag"
                        size="small"
                        options={filterOptions(availableTags, tags)}
                        value={tag}
                        handleChange={handleTagSelectionChange}
                        sx={{ width: '100%' }}
                        label={'Add a tag'}
                        id="tag-select"
                        onTextFieldChange={handleTagChange}
                        onBlur={saveCustomTag}
                        clearFunction={() => {
                            setState({ tag: '' })
                        }}
                        additionalFunction={saveCustomTag}
                        customIcon={
                            <Tooltip title="Add Tag">
                                <AddIcon />
                            </Tooltip>
                        }
                    />
                </Box>
            ) : (
                <Tooltip title="Add tags">
                    <IconButton
                        size="small"
                        onClick={handleStartTagging}
                        sx={{ color: 'primary.main' }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    )
}

export default UniversalTagToolbar
